import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "providers/auth";
import { trackEvent } from "utility/mixpanel";
import useFeatureFlags from "apps/user/learner/hooks/use-feature-flags";
import useWebOriEnabled from "apps/user/learner/hooks/use-web-ori-enabled";
import { setOriStarterMessage } from "apps/user/learner/lib/ori-starter-message";
import {
  claimStarterImpression,
  resolveSessionStarter,
} from "apps/user/learner/lib/ori-conversation-starter-storage";
import OriConversationStarterBanner from "./index";

const ENTRY_POINT = "conversation_starter";
const SURFACE = "web";

const OriConversationStarterContainer: React.FC = () => {
  const navigate = useNavigate();
  const { learner } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const { enabled: oriEnabled } = useWebOriEnabled();

  const learnerId = learner?.id ?? null;
  const gateOpen = oriEnabled && isEnabled("ori_conversation_starter");

  // Resolve once per page load (cached for the JS runtime), so the tip stays
  // stable across client-side navigation and only one tip is consumed per
  // session; a hard reload starts a fresh runtime and rotates to the next.
  const starter = useMemo(() => {
    if (!gateOpen || !learnerId) return null;
    return resolveSessionStarter(learnerId);
  }, [gateOpen, learnerId]);

  useEffect(() => {
    if (!starter || !learnerId) return;
    if (claimStarterImpression(learnerId)) {
      trackEvent("Conversation Starter Impression", {
        "Card ID": starter.id,
        Surface: SURFACE,
      });
    }
  }, [starter, learnerId]);

  if (!starter) return null;

  const handleTellMeMore = () => {
    trackEvent("Conversation Starter Clicked", {
      "Card ID": starter.id,
      "Entry Point": ENTRY_POINT,
      Surface: SURFACE,
    });
    setOriStarterMessage(starter.syntheticMessage, ENTRY_POINT);
    navigate("/user/chat");
  };

  return (
    <OriConversationStarterBanner
      starter={starter}
      onTellMeMore={handleTellMeMore}
    />
  );
};

export default OriConversationStarterContainer;
